import styled from "styled-components";

export const Page = styled.div`
  height: 100%;
  overflow-y: auto;
  background: #f3f4f7;
  color: #1b2028;
  font-family: "Manrope", sans-serif;

  @media print {
    overflow: visible;
    background: #fff;
  }
`;

export const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 18px 28px 12px;
  background: #fff;
  border-bottom: 1px solid #e3e8ef;

  @media print {
    position: static;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  line-height: 30px;
  font-weight: 700;
`;

export const Subtle = styled.span`
  color: #66758a;
  font-size: 12px;
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;

  @media print {
    display: none;
  }
`;

export const Chip = styled.button`
  border: 1px solid ${({ $active, $color }) => ($active ? $color || "#0C53BB" : "#dde2e9")};
  background: ${({ $active, $color }) => ($active ? `${$color || "#0C53BB"}14` : "#fff")};
  color: ${({ $active, $color }) => ($active ? $color || "#0C53BB" : "#48566b")};
  border-radius: 999px;
  padding: 3px 10px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 120ms ease;

  &:hover {
    border-color: ${({ $color }) => $color || "#0C53BB"};
  }
`;

export const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: inline-block;
`;

export const Body = styled.div`
  padding: 20px 28px 48px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(12, minmax(0, 1fr));

  @media (max-width: 1100px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

export const Panel = styled.section`
  grid-column: span ${({ $span }) => $span || 12};
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 12px;
  padding: 16px 18px 14px;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 1100px) {
    grid-column: span 6;
  }

  @media print {
    break-inside: avoid;
    border-color: #ccc;
  }
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

export const PanelHint = styled.span`
  color: #7688a3;
  font-size: 12px;
`;

/* The scorecard: one row per practice area, the page's top level. */
export const Scorecard = styled.section`
  grid-column: span 12;
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 12px;
  padding: 6px 6px 2px;
  overflow-x: auto;
  min-width: 0;

  @media (max-width: 1100px) {
    grid-column: span 6;
  }

  @media print {
    break-inside: avoid;
  }
`;

export const ScoreTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-variant-numeric: tabular-nums;

  th {
    text-align: left;
    color: #66758a;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 10px 12px 8px;
    border-bottom: 1px solid #e3e8ef;
    white-space: nowrap;
  }
  th.num,
  td.num {
    text-align: right;
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f0f3f7;
    vertical-align: top;
    white-space: nowrap;
  }
  th small {
    display: block;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
    color: #98a6ba;
  }
  tr:last-child td {
    border-bottom: 0;
  }
  tbody tr {
    cursor: pointer;
  }
  tbody tr:hover td {
    background: #f7f9fc;
  }
  tbody tr.selected td {
    background: #f1f6fe;
  }
  tbody tr.selected td:first-child {
    box-shadow: inset 3px 0 0 ${(p) => p.$accent || "#0C53BB"};
  }
  td:first-child {
    font-weight: 700;
    font-size: 14px;
  }
`;

export const ScoreTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

export const ScoreSpark = styled.div`
  width: 96px;
  height: 30px;
  flex: 0 0 auto;
`;

export const ScoreValue = styled.div`
  font-size: 22px;
  line-height: 26px;
  font-weight: 700;
  color: #1b2028;
`;

export const ScoreSub = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ $tone }) => ($tone === "good" ? "#1f7a4d" : $tone === "bad" ? "#b42318" : "#66758a")};
`;

export const ScoreHint = styled.div`
  padding: 6px 12px 8px;
  font-size: 12px;
  color: #7688a3;
`;

export const KpiDelta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ $tone }) => ($tone === "good" ? "#1f7a4d" : $tone === "bad" ? "#b42318" : "#66758a")};
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const Outcomes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
`;

export const Outcome = styled.button`
  text-align: left;
  font: inherit;
  background: #f7f9fc;
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
  &:hover {
    border-color: #0c53bb;
  }
  strong {
    display: block;
    font-size: 18px;
    font-variant-numeric: tabular-nums;
  }
  span {
    font-size: 12px;
    color: #66758a;
  }
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-top: 6px;
`;

export const LegendItem = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: ${({ $muted }) => ($muted ? "#98a6ba" : "#48566b")};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: #1b2028;
  }
`;

export const MiniTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  th {
    text-align: left;
    color: #66758a;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 6px 8px;
    border-bottom: 1px solid #e3e8ef;
    white-space: nowrap;
  }

  td {
    padding: 7px 8px;
    border-bottom: 1px solid #f0f3f7;
    white-space: nowrap;
  }

  td.num,
  th.num {
    text-align: right;
  }

  tr.clickable {
    cursor: pointer;
  }

  tr.clickable:hover td {
    background: #f5f8fd;
  }

  tr.active td {
    background: #eef4ff;
  }
`;

export const Arrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  font-size: 12px;
  color: ${({ $dir }) => ($dir === "up" ? "#b42318" : $dir === "down" ? "#1f7a4d" : "#7688a3")};
`;

export const Bar = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${({ $color }) => $color || "#0C53BB"};
  width: ${({ $pct }) => `${Math.max($pct, 1)}%`};
  min-width: 2px;
`;

export const BarTrack = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: #f0f3f7;
  width: 100%;
`;

export const Stack = styled.div`
  display: flex;
  height: 8px;
  gap: 1px;
  width: 100%;
`;

export const Note = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #f3dfa3;
  color: #6b4d00;
  font-size: 12px;
`;

export const Empty = styled.div`
  color: #7688a3;
  font-size: 13px;
  padding: 24px 0;
  text-align: center;
`;

export const NoAccess = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: #48566b;
  font-family: "Manrope", sans-serif;

  h2 {
    margin: 0 0 8px;
    color: #1b2028;
  }
`;
